import { Shield, Target, Zap, Flame, Crown, Users, Trophy, Star, Award, Swords, Activity, Lock } from "lucide-react";
import { useState, useMemo } from "react";
import { Loader } from "../../common/loader";
import { Badge } from "@/api/badges";

interface Achievement {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    unlocked: boolean;
    unlockedBy: string[];
    rarity: "common" | "rare" | "epic" | "legendary";
}

interface AchievementBadgesProps {
    badges: Badge[];
    isLoading?: boolean;
}

const rarityConfig = {
    common: {
        badge: "rarity-common-badge",
        icon: "rarity-common-icon",
    },
    rare: {
        badge: "rarity-rare-badge",
        icon: "rarity-rare-icon",
    },
    epic: {
        badge: "rarity-epic-badge",
        icon: "rarity-epic-icon",
    },
    legendary: {
        badge: "rarity-legend-badge",
        icon: "rarity-legend-icon",
    },
};

const getIconForAchievement = (title: string): React.ReactNode => {
    const t = title.toLowerCase();
    if (t.includes("offensive") || t.includes("juggernaut")) return <Zap className="w-4 h-4" />;
    if (
        t.includes("defensive") ||
        t.includes("fortress") ||
        t.includes("wall") ||
        t.includes("anchor") ||
        t.includes("general")
    )
        return <Shield className="w-4 h-4" />;
    if (t.includes("team") || t.includes("teamwork") || t.includes("playmaker") || t.includes("maestro"))
        return <Users className="w-4 h-4" />;
    if (t.includes("effort") || t.includes("battalion")) return <Flame className="w-4 h-4" />;
    if (t.includes("mvp") || t.includes("dominator")) return <Crown className="w-4 h-4" />;
    if (t.includes("performer") || t.includes("trophy") || t.includes("squad")) return <Trophy className="w-4 h-4" />;
    if (t.includes("bonus") || t.includes("master") || t.includes("star")) return <Star className="w-4 h-4" />;
    if (t.includes("balanced") || t.includes("award")) return <Award className="w-4 h-4" />;
    if (t.includes("force") || t.includes("storm") || t.includes("swords")) return <Swords className="w-4 h-4" />;
    if (t.includes("jack") || t.includes("activity")) return <Activity className="w-4 h-4" />;
    return <Target className="w-4 h-4" />;
};

export const AchievementBadges = ({ badges, isLoading = false }: AchievementBadgesProps) => {
    const [achievementType, setAchievementType] = useState<"individual" | "team">("individual");

    const { individualAchievements, teamAchievements } = useMemo(() => {
        const individual: Achievement[] = [];
        const team: Achievement[] = [];

        badges.forEach((badge) => {
            const rarity = badge.rarity.toLowerCase() as Achievement["rarity"];
            const unlocked = badge.players.length > 0;
            const icon = getIconForAchievement(badge.title);
            const entry: Achievement = {
                id: badge.title.toLowerCase().replace(/\s+/g, "_"),
                name: badge.title,
                description: badge.description,
                icon,
                unlocked,
                unlockedBy: badge.players,
                rarity,
            };
            if (badge.type === "Player") individual.push(entry);
            else team.push(entry);
        });

        const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 };
        const sort = (arr: Achievement[]) =>
            arr.sort((a, b) => {
                if (a.unlocked !== b.unlocked) return a.unlocked ? -1 : 1;
                return rarityOrder[b.rarity] - rarityOrder[a.rarity];
            });

        return { individualAchievements: sort(individual), teamAchievements: sort(team) };
    }, [badges]);

    const current = achievementType === "individual" ? individualAchievements : teamAchievements;
    const unlocked = current.filter((a) => a.unlocked);
    const locked = current.filter((a) => !a.unlocked);

    const isEmpty = !badges || badges.length === 0;

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex items-center justify-between mb-3 shrink-0">
                <div className="flex flex-col md:flex-row md:flex-wrap md:items-baseline md:gap-x-2">
                    <h3 className="text-lg font-headline font-bold section-title">Achievement Badges</h3>
                    {!isLoading && !isEmpty && (
                        <span className="text-sm font-normal text-on-surface-variant">
                            {unlocked.length}/{current.length} unlocked
                        </span>
                    )}
                </div>
                <div className="flex rounded-md p-0.5 gap-0.5 data-table-wrapper">
                    {(["individual", "team"] as const).map((type) => (
                        <button
                            key={type}
                            onClick={() => setAchievementType(type)}
                            className={`px-3 py-1 text-[10px] font-headline font-black uppercase tracking-widest rounded transition-all duration-150 cursor-pointer ${
                                achievementType === type
                                    ? "bg-white text-primary shadow-sm"
                                    : "text-on-surface-variant hover:text-on-surface"
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div
                    className="w-full flex-1 flex items-center justify-center bg-surface-container-lowest rounded-xl"
                    style={{ minHeight: "400px" }}
                >
                    <Loader message="Loading achievements..." />
                </div>
            ) : isEmpty ? (
                <div
                    className="w-full flex-1 flex items-center justify-center bg-surface-container-lowest rounded-xl"
                    style={{ minHeight: "400px" }}
                >
                    <p className="text-on-surface-variant">No achievement data available</p>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-auto rounded-xl data-table-wrapper">
                    <div className="data-table-header grid grid-cols-[1fr_auto] sm:grid-cols-[auto_1fr_auto] gap-4 px-4 sm:px-6 py-3 sticky top-0 z-10">
                        <span className="hidden sm:block font-headline font-black text-[10px] uppercase tracking-widest">
                            Badge
                        </span>
                        <span className="font-headline font-black text-[10px] uppercase tracking-widest">
                            Achievement
                        </span>
                        <span className="font-headline font-black text-[10px] uppercase tracking-widest text-right">
                            Earned by
                        </span>
                    </div>

                    <div className="data-table-body">
                        {unlocked.map((a, index) => {
                            const cfg = rarityConfig[a.rarity];
                            const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                            return (
                                <div
                                    key={a.id}
                                    className={`data-table-row grid grid-cols-[1fr_auto] sm:grid-cols-[auto_1fr_auto] gap-3 sm:gap-4 items-center px-4 sm:px-6 py-3 hover:bg-surface-container ${rowBg}`}
                                >
                                    <div
                                        className={`hidden sm:flex w-8 h-8 rounded-lg items-center justify-center shrink-0 ${cfg.icon}`}
                                    >
                                        {a.icon}
                                    </div>

                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-sm text-on-surface">{a.name}</span>
                                            <span
                                                className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${cfg.badge}`}
                                            >
                                                {a.rarity}
                                            </span>
                                        </div>
                                        <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-2 sm:line-clamp-1">
                                            {a.description}
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap justify-end gap-1 max-w-[100px] sm:max-w-[180px]">
                                        {achievementType === "team" ? (
                                            <span className="text-[10px] px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant font-medium whitespace-nowrap">
                                                Unlocked
                                            </span>
                                        ) : (
                                            <>
                                                {a.unlockedBy.slice(0, 2).map((player) => (
                                                    <span
                                                        key={player}
                                                        className="text-[10px] px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant font-medium whitespace-nowrap"
                                                    >
                                                        {player}
                                                    </span>
                                                ))}
                                                {a.unlockedBy.length > 2 && (
                                                    <span className="text-[10px] px-2 py-0.5 rounded bg-surface-container text-on-surface-variant">
                                                        +{a.unlockedBy.length - 2}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {locked.map((a, lockedIndex) => {
                            const globalIndex = unlocked.length + lockedIndex;
                            const rowBg =
                                globalIndex % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                            return (
                                <div
                                    key={a.id}
                                    className={`data-table-row grid grid-cols-[1fr_auto] sm:grid-cols-[auto_1fr_auto] gap-3 sm:gap-4 items-center px-4 sm:px-6 py-3 opacity-40 ${rowBg}`}
                                >
                                    <div className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center shrink-0 bg-surface-container-highest text-on-surface-variant">
                                        <Lock className="w-3.5 h-3.5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-sm text-on-surface-variant">{a.name}</span>
                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-surface-container-highest text-on-surface-variant">
                                                {a.rarity}
                                            </span>
                                        </div>
                                        <p className="text-xs text-on-surface-variant mt-0.5 line-clamp-1">
                                            {a.description}
                                        </p>
                                    </div>
                                    <span className="text-[10px] text-on-surface-variant">Locked</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
